import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ConfigProvider, Input, Select, Spin, Switch } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { useDebouncedValue } from 'services/hooks';
import { useSkillDetail } from 'core/hooks/Growth/useSkillDetail';

import {
  Colors,
  EmploymentTypeOptions,
  SeniorityOptions,
  SortOptions,
  WorkModeOptions,
} from 'context';
import { CareerIcon, ToastTypes } from 'atoms';
import { CareerVacancyCard, CareerVacancyDetails } from 'molecules';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { CareerLockedSection } from 'organisms';
import {
  formatSalaryRange,
  rememberApplyIntent,
  getCareerErrorText,
  useCareerSectionAccess,
  useCareerFilterDictionaries,
  useCareerVacancyFeed,
  useMarkVacancyInteraction,
  useMyCareerCenter,
  useToaster,
} from 'core';
import { useCareerJourney } from 'core/hooks/CareerJourney/useCareerJourney';
import { addApplicationHref, skillMapHref } from 'router/careerLinks';
import { PATHS } from 'router/config';
import { careerColor, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

import { vacancyEmptyKind } from './model/vacancyEmptyState';
import {
  activeVacancyFilters,
  clearVacancyFilters,
  IVacancyFilters,
  MATCH_MODE_OF,
  readVacancyFilters,
  readVacancyMode,
  readVacancySkillSlug,
  VACANCY_MODES,
  VACANCY_OFFER_PARAM,
  VACANCY_SKILL_PARAM,
  VacancyMode,
  withoutVacancyFilter,
  withVacancyFilter,
  withVacancyMode,
} from './model/vacancyFilterParams';

const INTERACTION_TOAST_KEY = 'careerVacancyInteraction';

/**
 * /career/jobs — the member's vacancy feed (spec 0018 §B2; ТЗ 0072, шаг 5).
 *
 * Filters (direction / work mode / seniority / debounced search) drive the
 * server-side query; every row's Save / Apply state is server truth
 * (`interaction`), and the mark mutation updates the normalized cache in place
 * so a click reflects without a refetch.
 */
export const CareerVacancies = memo(() => {
  const { t } = useTranslation();
  const { addToast } = useToaster();

  // ТЗ 0069: не-участник остаётся на странице и читает объяснение; лента
  // пропускается внутри `useCareerVacancyFeed`.
  const { isPreview } = useMyCareerCenter();
  const { locked, pending } = useCareerSectionAccess('vacancies');
  const { journey } = useCareerJourney();
  const hasGoal = Boolean(journey?.hasGoal);

  /**
   * 🔴 0072: единственный источник правды о ленте — АДРЕС. Режим подбора,
   * фильтры, навык и открытая карточка переживают переход на сайт работодателя
   * и «назад»; прежний `useState` терял всё, кроме `?skill=`.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = readVacancyMode(searchParams, hasGoal);
  const filters = readVacancyFilters(searchParams);
  const detailsId = searchParams.get(VACANCY_OFFER_PARAM);

  const replaceParams = useCallback(
    (next: URLSearchParams) => setSearchParams(next, { replace: true }),
    [setSearchParams],
  );
  const setFilter = <K extends keyof IVacancyFilters>(key: K, value: IVacancyFilters[K]) =>
    replaceParams(withVacancyFilter(searchParams, key, value));

  // Поиск печатается локально и уходит в адрес с задержкой: иначе каждая буква
  // — новая запись истории и новый запрос.
  const [search, setSearch] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(search);
  useEffect(() => {
    if (debouncedSearch.trim() === filters.search) return;
    replaceParams(withVacancyFilter(searchParams, 'search', debouncedSearch.trim()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  // Адрес сменили извне (сброс, «назад») — поле следует за ним.
  useEffect(() => {
    setSearch((prev) => (prev.trim() === filters.search ? prev : filters.search));
  }, [filters.search]);

  /**
   * Навык: слаг в адресе (`?skill=docker` из паспорта или клик по чипу), id для
   * запроса — из `careerSkillDetail`. Пока id не приехал, лента на паузе:
   * запрос без фильтра показал бы всю ленту, схлопывающуюся через мгновение.
   */
  const skillSlug = readVacancySkillSlug(searchParams);
  const { skill: skillFromUrl, loading: skillLoading } = useSkillDetail(skillSlug || '');
  const skillResolved = skillFromUrl && skillFromUrl.slug === skillSlug ? skillFromUrl : null;
  const skillPending = Boolean(skillSlug) && !skillResolved && skillLoading;
  const skillIds = skillResolved ? [skillResolved.id] : [];

  const {
    vacancies,
    totalCount,
    totalCountCapped,
    totalAvailable,
    matchContext,
    relevanceApplied,
    hasMore,
    loading,
    error,
    loadMore,
    refetch,
  } = useCareerVacancyFeed({
    directionId: filters.direction || undefined,
    cityId: filters.city || undefined,
    workMode: filters.workMode || undefined,
    seniority: filters.seniority || undefined,
    employmentType: filters.employment || undefined,
    skillIds,
    sort: filters.sort,
    search: filters.search,
    savedOnly: filters.saved,
    matchMode: MATCH_MODE_OF[mode],
    paused: skillPending,
  });
  const { directionOptions, cityOptions } = useCareerFilterDictionaries();

  // Клик по чипу навыка на карточке: один навык за раз, повторный клик снимает.
  const toggleSkill = useCallback(
    (skillId: string) => {
      const slug = vacancies.flatMap((row) => row.skills).find((row) => row.id === skillId)?.slug;
      if (!slug) return;
      const next = new URLSearchParams(searchParams);
      if (skillSlug === slug) next.delete(VACANCY_SKILL_PARAM);
      else next.set(VACANCY_SKILL_PARAM, slug);
      replaceParams(next);
    },
    [vacancies, searchParams, skillSlug, replaceParams],
  );

  const { markInteraction } = useMarkVacancyInteraction();
  // Per-vacancy in-flight set: a click on card B must not clear card A's
  // pending state, and both of a card's buttons stay disabled until its own
  // request resolves (guards the concurrent Save-then-Apply cache regression).
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const handleInteraction = useCallback(
    async (vacancyId: string, kind: 'saved' | 'applied') => {
      // Ранний выход, а не только скрытые кнопки: точек вызова три, и
      // забытая четвёртая молча ушла бы к серверу за отказом.
      if (isPreview) {
        return;
      }
      setPendingIds((prev) => new Set(prev).add(vacancyId));
      try {
        const result = await markInteraction(vacancyId, kind);
        if (!result.success) {
          addToast(INTERACTION_TOAST_KEY, {
            type: ToastTypes.danger,
            // По КОДУ, а не по тексту сервера: сообщение сервера русское.
            text: getCareerErrorText(t, result.errorCode),
          });
        }
      } catch {
        addToast(INTERACTION_TOAST_KEY, {
          type: ToastTypes.danger,
          text: t('careerCenter.vacancies.actionError'),
        });
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(vacancyId);
          return next;
        });
      }
    },
    [markInteraction, addToast, t, isPreview],
  );

  const active = activeVacancyFilters(filters, skillSlug);
  // «Только сохранённые» — это отдельный вопрос: пустой результат у него значит
  // «вы пока ничего не сохранили», а не «под фильтры ничего не нашлось».
  const savedOnlyIsTheOnlyFilter = filters.saved && active.length === 1;
  const emptyKind = vacancyEmptyKind({ savedOnlyIsTheOnlyFilter, totalAvailable });
  // Свёрнутый активный фильтр — невидимая причина пустой ленты: раскрываем.
  const hasHiddenFilters = Boolean(filters.workMode || filters.seniority || filters.employment);

  const labelOf = (key: typeof active[number]): string => {
    switch (key) {
      case 'direction':
        return String(
          directionOptions.find((row) => row.value === filters.direction)?.label ??
            t('careerCenter.vacancies.filters.direction'),
        );
      case 'city':
        return String(
          cityOptions.find((row) => row.value === filters.city)?.label ??
            t('careerCenter.vacancies.filters.city'),
        );
      case 'workMode':
        return t(`careerProfile.workMode.${filters.workMode}`, { defaultValue: filters.workMode });
      case 'seniority':
        return t(`careerProfile.seniority.${filters.seniority}`, {
          defaultValue: filters.seniority,
        });
      case 'employment':
        return t(`careerCenter.vacancies.employment.${filters.employment}`, {
          defaultValue: filters.employment,
        });
      case 'search':
        return t('careerCenter.vacancies.chips.search', { q: filters.search });
      case 'saved':
        return t('careerCenter.vacancies.filters.savedOnly');
      case 'skill':
        return skillResolved?.name ?? skillSlug ?? '';
      default:
        return '';
    }
  };

  /**
   * 0073: закрытие панели возвращает фокус туда, откуда её открыли. Панель
   * размонтируется сразу (без анимации), поэтому фокус переносится в
   * setTimeout — rAF в фоновой вкладке не вызывается.
   */
  const openerRef = useRef<HTMLElement | null>(null);
  const openDetails = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      const current = document.activeElement;
      openerRef.current = current instanceof HTMLElement ? current : null;
      next.set(VACANCY_OFFER_PARAM, id);
    } else {
      next.delete(VACANCY_OFFER_PARAM);
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener) {
        window.setTimeout(() => {
          if (opener.isConnected) opener.focus();
        }, 0);
      }
    }
    replaceParams(next);
  };

  const rememberApply = (row: {
    id: string;
    companyName: string;
    title: string;
    applyUrl: string;
  }) => {
    // Смотрящему методисту отклик завести негде и не за кого.
    if (isPreview) return;
    rememberApplyIntent({
      vacancyId: row.id,
      companyName: row.companyName,
      position: row.title,
      applyUrl: row.applyUrl,
    });
  };

  const skillHref = (slug: string) =>
    skillMapHref({ track: matchContext?.trackCode || null, skill: slug });
  // 0073: крестик очистки назван по-польски, а не «close-circle» из иконки antd.
  const clearIcon = <CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />;

  if (locked || pending) {
    return (
      <PageWrapper>
        {locked ? <CareerLockedSection capability="vacancies" /> : <Spin size="large" />}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>{t('careerCenter.vacancies.title')}</PageTitle>
        <PageInfo>{t('careerCenter.vacancies.info')}</PageInfo>
      </PageHeader>

      {/* 0072: один переключатель подбора вместо двух полос контекста. */}
      <ModeGroup role="radiogroup" aria-label={t('careerCenter.vacancies.mode.label')}>
        {VACANCY_MODES.map((key) => {
          const unavailable = key === 'goal' && !hasGoal;
          return (
            <ModeButton
              key={key}
              type="button"
              role="radio"
              aria-checked={mode === key}
              disabled={unavailable}
              $active={mode === key}
              onClick={() => replaceParams(withVacancyMode(searchParams, key))}>
              {t(MODE_LABEL[key])}
            </ModeButton>
          );
        })}
      </ModeGroup>
      <ModeHint>
        {mode === 'goal' && matchContext?.trackTitle
          ? t('careerCenter.vacancies.mode.goalHint', { title: matchContext.trackTitle })
          : mode === 'all'
          ? t('careerCenter.vacancies.mode.allHint')
          : t('careerCenter.vacancies.mode.profileHint')}
        {!hasGoal ? (
          <>
            {' '}
            <ContextLink to={PATHS.careerProfessions}>
              {t('careerCenter.vacancies.context.chooseGoal')}
            </ContextLink>
          </>
        ) : null}
      </ModeHint>

      {/* 0073: у каждого поля видимая подпись, связанная с ним (placeholder —
          не подпись), высота 44 и текст 14 через тему этого блока; при
          нехватке места сетка переносится, а не уменьшает шрифт. */}
      <ConfigProvider theme={{ token: { controlHeight: 44, fontSize: 14 } }}>
        <FiltersBar>
          {directionOptions.length ? (
            <Field>
              <FieldLabel htmlFor="vacancy-filter-direction">
                {t('careerCenter.vacancies.filters.direction')}
              </FieldLabel>
              <FilterSelect
                id="vacancy-filter-direction"
                allowClear
                clearIcon={clearIcon}
                value={filters.direction || undefined}
                placeholder={t('careerCenter.vacancies.filters.any')}
                options={directionOptions}
                onChange={(value) => setFilter('direction', (value as string) || '')}
              />
            </Field>
          ) : null}

          <Field>
            <FieldLabel htmlFor="vacancy-filter-city">
              {t('careerCenter.vacancies.filters.city')}
            </FieldLabel>
            <FilterSelect
              id="vacancy-filter-city"
              allowClear
              clearIcon={clearIcon}
              showSearch
              optionFilterProp="label"
              value={filters.city || undefined}
              placeholder={t('careerCenter.vacancies.filters.any')}
              options={cityOptions}
              onChange={(value) => setFilter('city', (value as string) || '')}
            />
          </Field>

          {/* В «Wszystkie oferty» порядок один — от новых: выбор «najpierw
              dopasowane» там обещал бы подбор, которого режим не делает. */}
          {mode === 'all' ? null : (
            <Field>
              <FieldLabel htmlFor="vacancy-filter-sort">
                {t('careerCenter.vacancies.sort.label')}
              </FieldLabel>
              <FilterSelect
                id="vacancy-filter-sort"
                value={filters.sort}
                // 0073: в режиме цели порядок — по цели, и подпись говорит об
                // этом, а не выдаёт его за совпадение навыков студента.
                options={
                  mode === 'goal'
                    ? [
                        { value: 'relevance', label: t('careerCenter.vacancies.sort.goal') },
                        { value: 'newest', label: t('careerCenter.vacancies.sort.newest') },
                      ]
                    : SortOptions
                }
                onChange={(value) =>
                  setFilter('sort', (value as string) === 'newest' ? 'newest' : 'relevance')
                }
              />
            </Field>
          )}

          <Field>
            <FieldLabel htmlFor="vacancy-filter-search">
              {t('careerCenter.vacancies.filters.search')}
            </FieldLabel>
            <SearchInput
              id="vacancy-filter-search"
              allowClear={{ clearIcon }}
              value={search}
              placeholder={t('careerCenter.vacancies.filters.searchPlaceholder')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Field>

          {/* «Только сохранённые» в предпросмотре — заведомо пустой фильтр. */}
          {isPreview ? null : (
            <SavedFilter>
              <Switch checked={filters.saved} onChange={(checked) => setFilter('saved', checked)} />
              {t('careerCenter.vacancies.filters.savedOnly')}
            </SavedFilter>
          )}
        </FiltersBar>

        {/* ТЗ 0069: редкие фильтры — под раскрытие; открыты, если хоть один выбран. */}
        <MoreFilters open={hasHiddenFilters}>
          <MoreFiltersSummary>{t('careerCenter.vacancies.filters.more')}</MoreFiltersSummary>
          <FiltersBar>
            <Field>
              <FieldLabel htmlFor="vacancy-filter-employment">
                {t('careerCenter.vacancies.filters.employment')}
              </FieldLabel>
              <FilterSelect
                id="vacancy-filter-employment"
                allowClear
                clearIcon={clearIcon}
                value={filters.employment || undefined}
                placeholder={t('careerCenter.vacancies.filters.any')}
                options={EmploymentTypeOptions}
                onChange={(value) => setFilter('employment', (value as string) || '')}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="vacancy-filter-work-mode">
                {t('careerCenter.vacancies.filters.workMode')}
              </FieldLabel>
              <FilterSelect
                id="vacancy-filter-work-mode"
                allowClear
                clearIcon={clearIcon}
                value={filters.workMode || undefined}
                placeholder={t('careerCenter.vacancies.filters.any')}
                options={WorkModeOptions}
                onChange={(value) => setFilter('workMode', (value as string) || '')}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="vacancy-filter-seniority">
                {t('careerCenter.vacancies.filters.seniority')}
              </FieldLabel>
              <FilterSelect
                id="vacancy-filter-seniority"
                allowClear
                clearIcon={clearIcon}
                value={filters.seniority || undefined}
                placeholder={t('careerCenter.vacancies.filters.any')}
                options={SeniorityOptions}
                onChange={(value) => setFilter('seniority', (value as string) || '')}
              />
            </Field>
          </FiltersBar>
        </MoreFilters>
      </ConfigProvider>

      {/* Активные ограничения — удаляемыми метками: причина пустой или короткой
          ленты всегда на виду и снимается одним нажатием. */}
      {active.length ? (
        <Chips aria-label={t('careerCenter.vacancies.chips.label')}>
          {active.map((key) => (
            <FilterChip
              key={key}
              type="button"
              aria-label={t('careerCenter.vacancies.chips.remove', { label: labelOf(key) })}
              onClick={() => replaceParams(withoutVacancyFilter(searchParams, key))}>
              {key === 'skill' && skillResolved ? (
                <SkillIcon
                  slug={skillResolved.slug}
                  name={skillResolved.name}
                  category={skillResolved.category}
                  iconUrl={skillResolved.iconUrl}
                  size={16}
                />
              ) : null}
              {labelOf(key)}
              <span aria-hidden>×</span>
            </FilterChip>
          ))}
          <InlineResetButton
            type="button"
            onClick={() => replaceParams(clearVacancyFilters(searchParams))}>
            {t('careerCenter.vacancies.resetFilters')}
          </InlineResetButton>
        </Chips>
      ) : null}

      {/* Слаг есть, а навыка такого в справочнике нет: показываем всю ленту и
          говорим почему. */}
      {skillSlug && !skillLoading && !skillResolved && !error ? (
        <Notice>{t('careerCenter.vacancies.skillFilterUnknown')}</Notice>
      ) : null}

      {!error && vacancies.length ? (
        <ResultsLine>
          <ResultsCount>
            {t('careerCenter.vacancies.count', {
              n: totalCountCapped ? `${totalCount}+` : totalCount,
            })}
          </ResultsCount>
          {/* 🔴 Ни слова о подборе, пока сервер не подтвердил ранжирование. */}
          {filters.sort === 'relevance' && mode !== 'all' ? (
            <RelevanceHint>
              {relevanceApplied
                ? t('careerCenter.vacancies.relevanceOn')
                : t('careerCenter.vacancies.relevanceOff')}
            </RelevanceHint>
          ) : null}
        </ResultsLine>
      ) : null}

      {error ? (
        <ErrorBox role="alert">
          <span>{t('careerCenter.vacancies.loadError')}</span>
          <SecondaryButton type="button" onClick={() => refetch()}>
            {t('careerCenter.vacancies.retry')}
          </SecondaryButton>
        </ErrorBox>
      ) : (loading || skillPending) && !vacancies.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : vacancies.length ? (
        <VacancyList>
          {vacancies.map((vacancy) => (
            <CareerVacancyCard
              key={vacancy.id}
              title={vacancy.title}
              companyName={vacancy.companyName}
              location={vacancy.location}
              workMode={vacancy.workMode}
              seniority={vacancy.seniority}
              employmentType={vacancy.employmentType}
              salary={formatSalaryRange(
                vacancy.salaryMin,
                vacancy.salaryMax,
                vacancy.salaryCurrency,
              )}
              description={vacancy.descriptionShort}
              directionTitles={vacancy.directions.map((direction) => direction.title)}
              skills={vacancy.skills}
              activeSkillIds={skillIds}
              onSkillClick={toggleSkill}
              match={vacancy.match}
              skillHref={skillHref}
              isNew={vacancy.isNew}
              applyUrl={vacancy.applyUrl}
              expiresAt={vacancy.expiresAt}
              publishedAt={vacancy.publishedAt}
              interaction={vacancy.interaction}
              canSave={!isPreview}
              actionLoading={pendingIds.has(vacancy.id)}
              onSave={() => handleInteraction(vacancy.id, 'saved')}
              onOpenDetails={() => openDetails(vacancy.id)}
              onApplyClick={() => rememberApply(vacancy)}
            />
          ))}
        </VacancyList>
      ) : (
        <EmptyState>
          {/* Три разных пустых экрана с разными выходами. */}
          {emptyKind === 'saved' ? (
            <>
              <EmptyTitle>{t('careerCenter.vacancies.emptySaved')}</EmptyTitle>
              <SecondaryButton
                type="button"
                onClick={() => replaceParams(withVacancyFilter(searchParams, 'saved', false))}>
                {t('careerCenter.vacancies.emptySavedAction')}
              </SecondaryButton>
            </>
          ) : emptyKind === 'filtered' ? (
            <>
              <EmptyTitle>{t('careerCenter.vacancies.emptyFiltered')}</EmptyTitle>
              <p>{t('careerCenter.vacancies.emptyWithoutFilters', { n: totalAvailable })}</p>
              {active.length ? (
                <PrimaryAction
                  type="button"
                  onClick={() => replaceParams(clearVacancyFilters(searchParams))}>
                  {t('careerCenter.vacancies.clearFilters')}
                </PrimaryAction>
              ) : null}
            </>
          ) : (
            <>
              <EmptyTitle>{t('careerCenter.vacancies.emptyBase')}</EmptyTitle>
              <p>{t('careerCenter.vacancies.emptyBaseLead')}</p>
              <EmptyActions>
                <ContextLink to={PATHS.careerLearning}>
                  {t('careerCenter.vacancies.emptyBasePrepare')}
                </ContextLink>
                <ContextLink to={addApplicationHref('sent')}>
                  {t('careerCenter.vacancies.emptyBaseJournal')}
                </ContextLink>
              </EmptyActions>
            </>
          )}
        </EmptyState>
      )}

      {hasMore && !error ? (
        <LoadMoreRow>
          <LoadMoreButton type="button" disabled={loading} onClick={loadMore}>
            {t('careerCenter.vacancies.loadMore')}
          </LoadMoreButton>
        </LoadMoreRow>
      ) : null}

      <CareerVacancyDetails
        vacancyId={detailsId}
        onClose={() => openDetails(null)}
        onSave={() => (detailsId ? handleInteraction(detailsId, 'saved') : undefined)}
        onApplyClick={rememberApply}
        preview={vacancies.find((item) => item.id === detailsId) ?? null}
        skillHref={skillHref}
        canSave={!isPreview}
        actionLoading={Boolean(detailsId && pendingIds.has(detailsId))}
      />
    </PageWrapper>
  );
});

const MODE_LABEL: Record<VacancyMode, string> = {
  goal: 'careerCenter.vacancies.mode.goal',
  profile: 'careerCenter.vacancies.mode.profile',
  all: 'careerCenter.vacancies.mode.all',
};

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const PageHeader = styled.div`
  margin: 16px 0 24px 0;
`;

const PageTitle = styled.h1`
  margin: 0;
  ${careerType.h1()};
`;

const PageInfo = styled.p`
  margin: 8px 0 0;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const ModeGroup = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${({ $active }) => ($active ? careerColor.active : 'transparent')};
  color: ${careerColor.text};
  cursor: pointer;
  ${({ $active }) => ($active ? careerType.bodyStrong() : careerType.body())};

  &:disabled {
    color: ${careerColor.text2};
    cursor: default;
  }
`;

const ModeHint = styled.p`
  margin: 8px 0 16px;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border: 1px solid ${careerColor.line};
  border-radius: 999px;
  background: ${careerColor.hover};
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.meta()};

  span[aria-hidden] {
    color: ${careerColor.text2};
    font-size: 16px;
  }
`;

const Notice = styled.p`
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${careerColor.hover};
  color: ${careerColor.text};
  ${careerType.meta()};
`;

const ErrorBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  padding: 16px;
  border: 1px solid ${Colors.signal_red};
  border-radius: ${careerLayout.cardRadius}px;
  color: ${careerColor.text};
  ${careerType.body()};
`;

const SecondaryButton = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.bodyStrong()};
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 24px;

  ${tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FieldLabel = styled.label`
  color: ${careerColor.text};
  ${careerType.meta()};
`;

const FilterSelect = styled(Select)`
  width: 100%;
` as typeof Select;

const SearchInput = styled(Input)`
  width: 100%;
`;

const VacancyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 48px 0;
  text-align: center;
  color: ${Colors.gray_dark};
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
`;

const EmptyTitle = styled.p`
  margin: 0;
  color: ${Colors.black};
  ${careerType.h3()};
`;

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 24px;
`;

const PrimaryAction = styled.button`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 24px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  cursor: pointer;
  ${careerType.bodyStrong()};
`;

const ContextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.hitTarget}px;
  color: ${Colors.black};
  text-decoration: underline;
  ${careerType.bodyStrong()};

  &:hover {
    color: ${Colors.black};
  }
`;

const ResetButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

/** Тот же сброс, что на пустом экране, но в строке результатов: там он нужен
 *  ДО того, как выборка схлопнется в ноль. */
const InlineResetButton = styled(ResetButton)`
  margin-left: auto;
`;

const SavedFilter = styled.label`
  display: inline-flex;
  align-items: center;
  align-self: end;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.meta()};
`;

const ResultsLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  margin-bottom: 12px;
`;

const ResultsCount = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 14, lineHeight: 143, weight: FontWeight.medium })}
`;

const RelevanceHint = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 13, lineHeight: 146 })}
`;

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const LoadMoreButton = styled.button`
  padding: 10px 24px;
  border: 1px solid ${Colors.main};
  border-radius: 8px;
  background: transparent;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fontService.getFont({ size: 14, lineHeight: 143, weight: FontWeight.medium })}
`;

const MoreFilters = styled.details`
  width: 100%;
`;

const MoreFiltersSummary = styled.summary`
  display: flex;
  align-items: center;
  min-height: ${careerLayout.hitTarget}px;
  cursor: pointer;
  margin-bottom: 8px;
  color: ${careerColor.text};
  ${careerType.meta()};

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;
